import { DatePicker, Form, Input } from "antd";
import { DATE_TIME_FORMAT } from "config/constants";
import { LOYALTY_FIELD_LIMITS, PROGRAM_FORM_MODE } from "features/LoyaltyPackage/core/constants";
import {
  getProgramDateRangePickerPanelValue,
  isProgramDateDisabled,
  toFormDateRangeMoment,
} from "features/LoyaltyPackage/core/utils";
import { t } from "i18next";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { LoyaltyModal, LoyaltyModalFooter, LoyaltyModalTextTitle } from "../../../components/LoyaltyModal";

import "../index.scss";

const { RangePicker } = DatePicker;

/** Wrapper moment ổn định cho antd 4 RangePicker — tránh crash rc-picker (date.month is not a function) */
function ProgramDateRangePicker({ value, onChange, onOpenChange, ...rest }) {
  const startMs = value?.[0]?.valueOf?.() ?? value?.[0]?._d?.valueOf?.();
  const endMs = value?.[1]?.valueOf?.() ?? value?.[1]?._d?.valueOf?.();
  const normalizedValue = useMemo(() => toFormDateRangeMoment(value), [startMs, endMs]); // eslint-disable-line react-hooks/exhaustive-deps

  const [openHackValue, setOpenHackValue] = useState(null);
  // Phía đang chọn ('start' | 'end') — chặn ngày kết thúc < hôm nay (start vẫn cho quá khứ)
  const [activeRange, setActiveRange] = useState("start");

  const displayValue = openHackValue ?? normalizedValue ?? null;

  const disabledDate = useCallback(
    (current) => {
      // Ngày kết thúc phải >= hôm nay; ngày bắt đầu cho phép quá khứ
      if (activeRange === "end") return isProgramDateDisabled(current);
      return false;
    },
    [activeRange]
  );

  const handleOpenChange = (open) => {
    if (open) {
      setActiveRange("start");
      const [start, end] = getProgramDateRangePickerPanelValue(normalizedValue);
      setOpenHackValue(
        normalizedValue ? [moment(normalizedValue[0].valueOf()), moment(normalizedValue[1].valueOf())] : [start, end]
      );
    } else {
      setOpenHackValue(null);
      setActiveRange("start");
    }
    onOpenChange?.(open);
  };

  const handleCalendarChange = (dates) => {
    // Đã chọn ngày bắt đầu, chưa chọn ngày kết thúc → chuyển sang chế độ chọn end (chặn quá khứ)
    setActiveRange(dates?.[0] && !dates?.[1] ? "end" : "start");
  };

  const handleChange = (dates) => {
    setOpenHackValue(null);
    if (!dates?.[0] || !dates?.[1]) {
      onChange?.(undefined);
      return;
    }
    // Chỉ chọn ngày — mặc định start 00:00:00, end 23:59:59
    const start = moment(dates[0].valueOf()).startOf("day");
    const end = moment(dates[1].valueOf()).endOf("day").milliseconds(0);
    onChange?.([start, end]);
  };

  return (
    <RangePicker
      {...rest}
      value={displayValue}
      onOpenChange={handleOpenChange}
      onCalendarChange={handleCalendarChange}
      onChange={handleChange}
      format={DATE_TIME_FORMAT}
      disabledDate={disabledDate}
    />
  );
}

function ProgramFormModal({ visible, mode, initialValues, onCancel, onSubmit }) {
  const [form] = Form.useForm();
  const initialValuesRef = useRef(initialValues);
  initialValuesRef.current = initialValues;

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      return;
    }

    const values = initialValuesRef.current || {};
    form.resetFields();
    form.setFieldsValue({
      ...values,
      dateRange: toFormDateRangeMoment(values.dateRange),
    });
  }, [visible, mode, form]);

  const isEditMode = mode === PROGRAM_FORM_MODE.EDIT;
  const title = isEditMode ? t("loyaltyPackage.programForm.editTitle") : t("loyaltyPackage.programForm.createTitle");
  const submitText = isEditMode ? t("loyaltyPackage.form.save") : t("loyaltyPackage.form.add");

  return (
    <LoyaltyModal
      title={<LoyaltyModalTextTitle>{title}</LoyaltyModalTextTitle>}
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      destroyOnClose
      className="loyalty-program-form-modal loyalty-modal--footer-in-body"
    >
      <Form form={form} layout="vertical" onFinish={onSubmit} className="loyalty-program-form" requiredMark={false}>
        <Form.Item
          label={
            <span className="loyalty-field-label">
              {t("loyaltyPackage.form.programName")} <span className="loyalty-required">*</span>
            </span>
          }
          name="title"
          rules={[
            { required: true, message: t("loyaltyPackage.programForm.nameRequired") },
            { max: LOYALTY_FIELD_LIMITS.NAME_MAX_LENGTH, message: t("loyaltyPackage.programForm.nameMaxLength") },
          ]}
        >
          <Input placeholder={t("loyaltyPackage.programForm.namePlaceholder")} className="loyalty-input-custom" />
        </Form.Item>

        <Form.Item
          label={
            <span className="loyalty-field-label">
              {t("loyaltyPackage.programForm.dateRangeLabel")} <span className="loyalty-required">*</span>
            </span>
          }
          name="dateRange"
          rules={[{ required: true, message: t("loyaltyPackage.programForm.dateRangeRequired") }]}
          getValueProps={(val) => ({ value: toFormDateRangeMoment(val) })}
          getValueFromEvent={(dates) => {
            if (!dates?.[0] || !dates?.[1]) return undefined;
            return [moment(dates[0].valueOf()), moment(dates[1].valueOf())];
          }}
        >
          <ProgramDateRangePicker
            className="loyalty-program-date-range-picker"
            style={{ width: "100%" }}
            placeholder={[
              t("loyaltyPackage.programForm.dateStartPlaceholder"),
              t("loyaltyPackage.programForm.dateEndPlaceholder"),
            ]}
          />
        </Form.Item>

        <Form.Item
          label={
            <span className="loyalty-field-label">
              {t("loyaltyPackage.programForm.termsUrl")} <span className="loyalty-required">*</span>
            </span>
          }
          name="termsUrl"
          rules={[{ required: true, message: t("loyaltyPackage.programForm.termsUrlRequired") }]}
        >
          <Input placeholder={t("loyaltyPackage.programForm.termsUrlPlaceholder")} />
        </Form.Item>

        <LoyaltyModalFooter inBody onCancel={onCancel} confirmLabel={submitText} confirmHtmlType="submit" />
      </Form>
    </LoyaltyModal>
  );
}

export default ProgramFormModal;
